/**
 * Compiles an exploration op-log into one graph-algebra pipeline.
 *
 * Spike findings (REMAP_API_HANDOFF.md):
 * - Supported ops: select_label, expand_to_label (optional rels filter), exclude_ids,
 *   project_graph, combine_handles (multi-label NodeSet accumulation).
 * - removeNode maps to exclude_ids (not "exclude").
 * - combine_handles replaces the old single-label `union` op; it accepts mixed-label
 *   handles and deduplicates automatically.
 * - plan_id is returned on success; patch semantics are undocumented — the UI
 *   resends the full compiled step list each run and stores plan_id for caching.
 */
import { REMAP_PIPELINE_NAMES } from '@/constants/pipelines.constants';
import { filterGraphByFilters } from '@/helpers/graphFilter.helpers';
import type { GraphFiltersByLabel } from '@/helpers/graphFilter.helpers';
import { getGraphBusinessId } from '@/helpers/graphIdentity.helpers';
import type { Pipeline, PipelineStep } from '@/interfaces/pipelines.interface';
import type { GraphData, GraphNode } from '@/types/graph.types';
import type { ExplorationOp } from '@/types/exploration.types';

const DEFAULT_MAX_NODES = 1000;
const DEFAULT_MAX_RELATIONSHIPS = 5000;

function seedIdsByLabelFromOps(ops: ExplorationOp[]): Record<string, string[]> {
  const idsByLabel: Record<string, string[]> = {};

  for (const op of ops) {
    if (op.kind === 'seedSingleNode') {
      idsByLabel[op.label] = idsByLabel[op.label] ?? [];
      if (!idsByLabel[op.label].includes(op.nodeId)) {
        idsByLabel[op.label].push(op.nodeId);
      }
    } else if (op.kind === 'seedFromGraph') {
      for (const [label, ids] of Object.entries(op.idsByLabel)) {
        idsByLabel[label] = [...new Set([...(idsByLabel[label] ?? []), ...ids])];
      }
    } else if (op.kind === 'seedByIds') {
      const grouped = op.idsByLabel ?? {};
      for (const [label, ids] of Object.entries(grouped)) {
        idsByLabel[label] = [...new Set([...(idsByLabel[label] ?? []), ...ids])];
      }
    }
  }

  return idsByLabel;
}

function effectiveSeedIdsByLabelFromOps(
  ops: ExplorationOp[],
): Record<string, string[]> {
  const removedIdSet = new Set(getRemovedNodeIds(ops));
  const idsByLabel = seedIdsByLabelFromOps(ops);
  for (const label of Object.keys(idsByLabel)) {
    idsByLabel[label] = idsByLabel[label].filter((id) => !removedIdSet.has(id));
  }
  return idsByLabel;
}

export function getRemovedNodeIds(ops: ExplorationOp[]): string[] {
  return ops
    .filter(
      (op): op is Extract<ExplorationOp, { kind: 'removeNode' }> =>
        op.kind === 'removeNode',
    )
    .map((op) => op.nodeId);
}

/**
 * Drops removed nodes (and their now-dangling relationships) from a pipeline
 * result as a client-side safety net.
 *
 * The compiled pipeline already applies `exclude_ids`, but an `expand_to_label`
 * step downstream can legitimately re-introduce a removed node as a freshly
 * expanded neighbour (this is why leaf nodes "wouldn't delete" — the backend
 * excluded then re-added them). Filtering here on business id guarantees a
 * removeNode op is always honoured in the rendered graph regardless of expand
 * ordering. Ids are compared via `getGraphBusinessId` to stay consistent with
 * how removeNode ops are recorded.
 */
export function filterGraphByRemovedIds(
  nodes: GraphNode[],
  relationships: GraphData['relationships'],
  removedIds: string[],
): GraphData {
  if (removedIds.length === 0) {
    return { nodes, relationships };
  }
  const removed = new Set(removedIds);
  const filteredNodes = nodes.filter(
    (node) => !removed.has(getGraphBusinessId(node)),
  );
  const activeIds = new Set(
    filteredNodes.map((node) => getGraphBusinessId(node)).filter(Boolean),
  );
  const filteredRelationships = relationships.filter(
    (rel) => activeIds.has(String(rel.from)) && activeIds.has(String(rel.to)),
  );
  return { nodes: filteredNodes, relationships: filteredRelationships };
}

/**
 * Counts nodes/relationships as the user sees them after removeNode pruning and
 * optional per-label table filters (same stack as the rendered graph).
 */
export function computeRenderedGraphCounts(
  graphData: GraphData,
  ops: ExplorationOp[],
  filters?: GraphFiltersByLabel,
): { nodeCount: number; relationshipCount: number } {
  const pruned = filterGraphByRemovedIds(
    graphData.nodes,
    graphData.relationships,
    getRemovedNodeIds(ops),
  );
  const rendered = filterGraphByFilters(
    pruned.nodes,
    pruned.relationships,
    filters,
  );
  return {
    nodeCount: rendered.nodes.length,
    relationshipCount: rendered.relationships.length,
  };
}

function buildSelectByIdsStep(
  label: string,
  ids: string[],
  as: string,
): PipelineStep {
  return {
    op: 'select_label',
    args: {
      label,
      predicates: [{ field: 'id', op: 'in', value: ids }],
    },
    as,
  };
}

function buildCombineHandlesStep(handles: string[], as: string): PipelineStep {
  return {
    op: 'combine_handles',
    handles,
    as,
  };
}

export interface CompileExplorationPipelineOptions {
  maxNodes?: number;
  maxRelationships?: number;
}

export function compileExplorationPipeline(
  ops: ExplorationOp[],
  options: CompileExplorationPipelineOptions = {},
): Pipeline {
  const {
    maxNodes = DEFAULT_MAX_NODES,
    maxRelationships = DEFAULT_MAX_RELATIONSHIPS,
  } = options;

  const steps: PipelineStep[] = [];
  const unionHandles: string[] = [];

  const removedIds = getRemovedNodeIds(ops);

  // Strip removed ids out of the seed selectors up front: if a removed node is
  // never re-selected as a seed, only `exclude_ids` (below) has to catch it,
  // and we avoid re-fetching a node the user explicitly deleted.
  const seedEntries = Object.entries(effectiveSeedIdsByLabelFromOps(ops)).filter(
    ([, ids]) => ids.length > 0,
  );

  seedEntries.forEach(([label, ids], index) => {
    const as = `seed_${index}`;
    steps.push(buildSelectByIdsStep(label, ids, as));
    unionHandles.push(as);
  });

  const expandOps = ops.filter(
    (op): op is Extract<ExplorationOp, { kind: 'expandToLabel' }> =>
      op.kind === 'expandToLabel',
  );

  expandOps.forEach((op, index) => {
    const sourceAs = `expand_src_${index}`;
    steps.push({
      op: 'select_label',
      args: {
        label: op.sourceLabel,
        predicates: [{ field: 'id', op: '=', value: op.nodeId }],
      },
      as: sourceAs,
    });

    const expandArgs: Record<string, unknown> = {
      target_label: op.targetLabel,
      direction: 'both',
    };
    if (op.relTypes && op.relTypes.length > 0) {
      expandArgs.rels = op.relTypes;
    }

    const expandAs = `expand_${index}`;
    steps.push({
      op: 'expand_to_label',
      from: sourceAs,
      args: expandArgs,
      as: expandAs,
    });
    unionHandles.push(expandAs);
  });

  if (unionHandles.length === 0) {
    throw new Error('Exploration pipeline requires at least one seed or expand op');
  }

  const combinedAs =
    unionHandles.length === 1 ? unionHandles[0] : 'exploration_combined';

  if (unionHandles.length > 1) {
    steps.push(buildCombineHandlesStep(unionHandles, combinedAs));
  }

  let graphFrom = combinedAs;
  if (removedIds.length > 0) {
    const filteredAs = 'exploration_filtered';
    steps.push({
      op: 'exclude_ids',
      from: combinedAs,
      args: { ids: removedIds },
      as: filteredAs,
    });
    graphFrom = filteredAs;
  }

  steps.push({
    op: 'project_graph',
    from: graphFrom,
    args: {
      max_nodes: maxNodes,
      max_relationships: maxRelationships,
      // INDUCED: the accumulated op-log IS the population the user built
      // (seeds + each explicit expand), so draw those nodes and only the
      // edges between them. The default open 1-hop neighbourhood re-adds
      // every unrequested neighbour of every node — and one hub node (an
      // Invoice with tens of thousands of incident charge/status edges)
      // exhausts max_nodes on its own, so the rendered graph collapsed
      // into an arbitrary slice of that hub instead of the explored set.
      induced: true,
    },
    as: 'graph',
  });

  return {
    name: REMAP_PIPELINE_NAMES.EXPLORATION,
    steps,
    emit: 'last',
  };
}

/**
 * When a compiled exploration pipeline is a pure *seed projection* (it has no
 * `expand_to_label` step), reMap renders it as isolated seed nodes — the live
 * view and saved-view restore both show only the seeds with no auto 1-hop
 * neighbourhood (see `needsPipelineReplay` / `buildSeedGraphFromOps`).
 *
 * `project_graph`, however, materialises each seed's incident relationships and
 * neighbour nodes, so a re-run returns MORE than the user saw. This returns the
 * seed business ids so callers can prune the projection back to the seed set
 * (dropping the spurious neighbourhood). Returns `null` when the pipeline
 * contains an expand step, meaning the full projected graph should be kept.
 */
export function readSeedOnlyProjectionIds(
  pipeline: Pipeline | undefined,
): string[] | null {
  const steps = pipeline?.steps ?? [];
  if (steps.length === 0) return null;

  const hasExpand = steps.some((step) => step.op === 'expand_to_label');
  if (hasExpand) return null;

  const ids: string[] = [];
  for (const step of steps) {
    if (step.op !== 'select_label') continue;
    const predicates = (step.args?.predicates ?? []) as Array<{
      field?: string;
      op?: string;
      value?: unknown;
    }>;
    for (const predicate of predicates) {
      if (
        predicate.field === 'id' &&
        predicate.op === 'in' &&
        Array.isArray(predicate.value)
      ) {
        for (const value of predicate.value) ids.push(String(value));
      }
    }
  }

  return ids;
}

export function hasExplorationPipelineOps(ops: ExplorationOp[]): boolean {
  return ops.some(
    (op) =>
      op.kind === 'expandToLabel' ||
      op.kind === 'removeNode' ||
      (op.kind === 'seedByIds' && op.idsByLabel),
  );
}

/**
 * Returns true when `compileExplorationPipeline` would produce a valid pipeline,
 * i.e. the op-log still has at least one seed (after removed ids are stripped)
 * or expand op. A log of only `removeNode` ops — or one where every seed was
 * removed — is NOT compilable; compiling it throws. Use this to gate pipeline
 * persistence.
 */
export function canCompileExplorationPipeline(ops: ExplorationOp[]): boolean {
  const hasSeed = Object.values(effectiveSeedIdsByLabelFromOps(ops)).some(
    (ids) => ids.length > 0,
  );
  const hasExpand = ops.some((op) => op.kind === 'expandToLabel');
  return hasSeed || hasExpand;
}
